import React, { useState } from 'react';
import { 
  Box, Paper, Typography, Button, CircularProgress, Alert, IconButton, Tooltip
} from '@mui/material';
import { 
  AutoAwesome as SparklesIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../api/api';

export const MemoriaFacturacionAlert = ({ idCliente, idPaciente }) => {
  const [loading, setLoading] = useState(false);
  const [memoria, setMemoria] = useState(null);
  const [consultado, setConsultado] = useState(false);

  const consultarUltimoReceptor = async () => {
    if (!idCliente && !idPaciente) return;

    setLoading(true);
    setConsultado(true);
    try {
      const queryParam = idCliente 
        ? `id_cliente=${idCliente}` 
        : `id_paciente=${idPaciente}`;

      const res = await api.get(`/documentos_fiscales/ultimo-receptor?${queryParam}`);
      setMemoria(res.data);
    } catch (err) {
      console.error("Error al consultar datos de facturación previos:", err);
      setMemoria({ tiene_memoria: false });
    } finally {
      setLoading(false);
    }
  };

  const handleCerrar = () => {
    setConsultado(false);
    setMemoria(null);
  };

  // 1. Estado inicial: Muestra el botón para consultar manualmente
  if (!consultado) {
    return (
      <Button
        variant="outlined"
        color="warning"
        size="small"
        startIcon={loading ? <CircularProgress size={16} /> : <SparklesIcon />}
        onClick={consultarUltimoReceptor}
        disabled={loading || (!idCliente && !idPaciente)}
        sx={{ mt: 1 }}
      >
        {loading ? "Consultando..." : "Buscar Memoria de Facturación Previas"}
      </Button>
    );
  }

  // 2. Cargando
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, p: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Buscando registros de facturación anteriores...
        </Typography>
      </Box>
    );
  }

  // 3. Resultado: Sin datos previos registrados
  if (!memoria || !memoria.tiene_memoria) {
    return (
      <Alert severity="info" sx={{ my: 1 }} onClose={handleCerrar}>
        No se encontraron registros de facturación/DTE anteriores para este expediente.
      </Alert>
    );
  }

  const { datos_dte } = memoria;

  // 4. Resultado: Muestra los datos encontrados con botón de cierre
  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 2, 
        my: 1.5, 
        bgcolor: '#fffbe6', 
        borderColor: '#ffe58f', 
        borderRadius: 2 
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, borderBottom: '1px solid #ffe58f', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SparklesIcon sx={{ color: '#d48806', fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight="bold" color="#614700">
            Datos de Facturación Previos Encontrados
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {datos_dte.numero_control && (
            <Typography variant="caption" sx={{ bg: '#fff0f6', px: 1, py: 0.5, borderRadius: 1, fontWeight: 'medium', border: '1px solid #ffd591' }}>
              Comprobante: {datos_dte.numero_control}
            </Typography>
          )}
          <Tooltip title="Cerrar">
            <IconButton size="small" onClick={handleCerrar} sx={{ color: '#614700', p: 0.25 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Información utilizada en la última emisión fiscal realizada para este cliente/paciente:
      </Typography>

      <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, border: '1px solid #f0f0f0', fontFamily: 'monospace', fontSize: '0.825rem' }}>
        <div><strong>Receptor:</strong> {datos_dte.nombre_receptor || datos_dte.facturado_a_nombre_de || 'N/A'}</div>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 0.5 }}>
          {datos_dte.nit_receptor && <div><strong>NIT:</strong> {datos_dte.nit_receptor}</div>}
          {datos_dte.dui_receptor && <div><strong>DUI:</strong> {datos_dte.dui_receptor}</div>}
          {datos_dte.nrc_receptor && <div><strong>NRC:</strong> {datos_dte.nrc_receptor}</div>}
          {datos_dte.pasaporte_receptor && <div><strong>Pasaporte:</strong> {datos_dte.pasaporte_receptor}</div>}
        </Box>
      </Box>
    </Paper>
  );
};

export default MemoriaFacturacionAlert;